<template>
  <SignInSignUpLayout>
    <Preloader v-if="ui.isLoading" />

    <template v-if="!passwordChangedSuccessfully">
      <div v-if="userIsAllowedToUpdatePassword === false">
        <h1 class="sign-in-title">Token is invalid</h1>
      </div>

      <b-form v-if="userIsAllowedToUpdatePassword !== false" class="sing-in-form" @submit="handleFormSubmit">
        <h1 class="sign-in-title">Reset password</h1>

        <PasswordField
          v-model="password"
          id="password-1"
          label="New password"
          placeholder="Enter new password"
          autocomplete="new-password"
          :state="passwordState"
        >
          <template #invalid-feedback>
            <div v-for="(error, index) in passwordServerErrors" :key="index">
              {{ error }}
            </div>
          </template>
        </PasswordField>

        <PasswordField
          v-model="passwordRepeat"
          id="password-2"
          label="Confirm password"
          placeholder="Confirm password"
          autocomplete="new-password"
          :state="passwordRepeatState"
        >
          <template #invalid-feedback>
            <div v-for="(error, index) in passwordRepeatServerErrors" :key="index">
              {{ error }}
            </div>
          </template>
        </PasswordField>

        <PasswordRequirements
          :hideStatuses="!submitButtonPressed"
          :hasMinLength="validationStatus.hasMinLength"
          :hasUppercaseAndLowercase="validationStatus.hasUppercase && validationStatus.hasLowercase"
          :hasNumbers="validationStatus.hasNumbers"
          :hasSpecialChars="validationStatus.hasSpecialChars"
        >
          <template #additional-requirements>
            <PasswordRequirementRow :isValid="!submitButtonPressed ? null : validationStatus.passwordsEqual">
              Passwords in both fields should match
            </PasswordRequirementRow>
          </template>
        </PasswordRequirements>

        <b-button type="submit" variant="primary" class="button" :disabled="isSubmitButtonDisabled">Change password</b-button>
      </b-form>
    </template>

    <SuccessView v-else title="Password changed successfully!">
      <p>Please sign-in to your account</p>

      <template #button>
        <b-button
          tag="router-link"
          :to="{ name: ROUTES.SIGN_IN.name }"
          variant="primary"
          class="button sign-in-button"
          :disabled="isSubmitButtonDisabled"
        >
          Sign-in
        </b-button>
      </template>
    </SuccessView>
  </SignInSignUpLayout>
</template>

<script>
import Preloader from '@components/Preloader.vue'
import SignInSignUpLayout from '@components/sign-up/SignInSignUpLayout.vue'
import PasswordRequirements from '@components/sign-up/PasswordRequirements.vue'
import PasswordRequirementRow from '@components/sign-up/PasswordRequirementRow.vue'
import PasswordField from '@components/sign-up/PasswordField.vue'
import SuccessView from '@components/sign-up/SuccessView.vue'
import { validatePassword } from '@components/sign-up/validatePassword'

export default {
  name: 'PasswordUpdate',
  components: {
    Preloader,
    SignInSignUpLayout,
    PasswordRequirements,
    PasswordRequirementRow,
    PasswordField,
    SuccessView
  },
  data () {
    return {
      token: null,
      userIsAllowedToUpdatePassword: null,
      password: '',
      passwordRepeat: '',

      passwordServerErrors: null,
      passwordRepeatServerErrors: null,

      submitButtonPressed: false,
      resetRequestPending: false,
      passwordChangedSuccessfully: false,

      ui: {
        isLoading: false
      }
    }
  },
  computed: {
    isSubmitButtonDisabled () {
      if (
        this.submitButtonPressed &&
        (!this.validationStatus.isValid || this.passwordServerErrors || this.passwordRepeatServerErrors)
      ) {
        return true
      }

      return this.resetRequestPending
    },
    validationStatus () {
      const status = validatePassword(this.password)

      status.passwordsEqual = this.password === this.passwordRepeat
      status.isValid = status.isValid && status.passwordsEqual

      return status
    },
    passwordState () {
      if (!this.submitButtonPressed) {
        return null
      }

      return this.validationStatus.isValid && !this.passwordServerErrors ? null : false
    },
    passwordRepeatState () {
      if (!this.submitButtonPressed) {
        return null
      }

      return this.validationStatus.passwordsEqual && !this.passwordRepeatServerErrors ? null : false
    }
  },
  mounted () {
    this.ui.isLoading = true

    this.axios({
      method: 'get',
      url: `/api/password/verified`,
      headers: {}
    })
      .then(() => {
        this.userIsAllowedToUpdatePassword = true
      })
      .catch(() => {
        this.userIsAllowedToUpdatePassword = false
      })
      .finally(() => {
        this.ui.isLoading = false
      })
  },
  methods: {
    handleFormSubmit (e) {
      e.preventDefault()

      this.submitButtonPressed = true
      this.passwordServerErrors = null
      this.passwordRepeatServerErrors = null

      if (!this.validationStatus.isValid) {
        return
      }

      this.resetRequestPending = true

      this.axios.post(
        `/api/password/restore`,
        { password: this.password, new_password: this.passwordRepeat },
        {
          headers: {
            Authorization: 'Bearer' + localStorage.getItem('access_token')
          }
        })
        .then(() => {
          this.passwordChangedSuccessfully = true
        })
        .catch(error => {
          if (error.response?.data?.errors) {
            // eslint-disable-next-line camelcase
            const { password, new_password, ...restErrors } = error.response.data.errors

            if (password) this.passwordServerErrors = password
            // eslint-disable-next-line camelcase
            if (new_password) this.passwordRepeatServerErrors = new_password

            const errors = Object.values(restErrors)

            for (let i = 0; i < errors.length; i++) {
              this.$snotify.error(errors[i][0], 'Error', this.$store.state.snotify)
            }
          } else {
            this.$snotify.error(error.response?.data?.error ?? error, 'Error', this.$store.state.snotify)
          }
        })
        .finally(() => {
          this.resetRequestPending = false
        })
    }
  }
}
</script>

<style lang="scss" scoped>
.password-changed-title {
  font-size: 24px;
  margin-bottom: 5px;
}

.sign-in-button.button {
  min-width: 360px;
}
</style>
